/**
 * ADMIN ROLE & PERMISSION MANAGER
 * 
 * Comprehensive UI for managing admin roles, permissions, and role-permission assignments.
 * Allows super admins to:
 * - View all roles and their permissions
 * - Create/edit/delete roles
 * - Assign/remove permissions to/from roles
 * - View which pages each role can access
 */

import React, { useState, useEffect } from 'react'
import { getAllRoles, getAllPermissions, getRolePermissions, PAGE_PERMISSIONS } from '../../lib/adminPermissions'
import { logAdminAction } from '../../lib/adminAuth'
import { getSessionHeaders } from '../../lib/serverSession';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface Role {
  id: string
  role_name: string
  display_name: string
  description: string
  is_system_role: boolean
}

interface Permission {
  id: string
  permission_key: string
  display_name: string
  description: string
  category: string
}

interface AdminRolePermissionManagerProps {
  currentUser: any
}

export default function AdminRolePermissionManager({ currentUser }: AdminRolePermissionManagerProps) {
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [rolePermissions, setRolePermissions] = useState<Record<string, string[]>>({})
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'roles' | 'permissions' | 'assignments'>('roles')
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false)
  const [showEditRoleModal, setShowEditRoleModal] = useState(false)
  const [showPermissionAssignModal, setShowPermissionAssignModal] = useState(false)

  // Form state for role creation/editing
  const [roleFormData, setRoleFormData] = useState({
    role_name: '',
    display_name: '',
    description: ''
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      
      // Load roles
      const rolesData = await getAllRoles()
      setRoles(rolesData)

      // Load permissions
      const permissionsData = await getAllPermissions()
      setPermissions(permissionsData)

      // Load role-permission mappings
      const rolePermMap: Record<string, string[]> = {}
      for (const role of rolesData) {
        const perms = await getRolePermissions(role.role_name)
        rolePermMap[role.id] = perms
      }
      setRolePermissions(rolePermMap)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateRole = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'create_role',
          data: {
            roleName: roleFormData.role_name,
            displayName: roleFormData.display_name,
            description: roleFormData.description,
            isSystemRole: false
          }
        })
      })
      const result = await response.json()

      if (!response.ok) throw new Error(result.error)

      await logAdminAction(currentUser.email, 'create_role', 'admin_roles', result.data?.id, {
        role_name: roleFormData.role_name,
        display_name: roleFormData.display_name
      })

      setShowCreateRoleModal(false)
      setRoleFormData({ role_name: '', display_name: '', description: '' })
      loadData()
    } catch (error: any) {
      console.error('Error creating role:', error)
      alert(`Error creating role: ${error.message}`)
    }
  }

  const handleUpdateRole = async () => {
    if (!selectedRole) return

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'update_role',
          data: {
            roleId: selectedRole.id,
            displayName: roleFormData.display_name,
            description: roleFormData.description
          }
        })
      })
      const result = await response.json()

      if (!response.ok) throw new Error(result.error)

      await logAdminAction(currentUser.email, 'update_role', 'admin_roles', selectedRole.id, {
        display_name: roleFormData.display_name
      })

      setShowEditRoleModal(false)
      setSelectedRole(null)
      setRoleFormData({ role_name: '', display_name: '', description: '' })
      loadData()
    } catch (error: any) {
      console.error('Error updating role:', error)
      alert(`Error updating role: ${error.message}`)
    }
  }

  const handleDeleteRole = async (role: Role) => {
    if (role.is_system_role) {
      alert('Cannot delete system roles')
      return
    }

    if (!confirm(`Are you sure you want to delete the role "${role.display_name}"? This will remove all permission assignments for this role.`)) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'delete_role',
          data: { roleId: role.id }
        })
      })
      const result = await response.json()

      if (!response.ok) throw new Error(result.error)

      await logAdminAction(currentUser.email, 'delete_role', 'admin_roles', role.id, {
        role_name: role.role_name
      })

      loadData()
    } catch (error: any) {
      console.error('Error deleting role:', error)
      alert(`Error deleting role: ${error.message}`)
    }
  }

  const handleTogglePermission = async (roleId: string, permissionId: string, currentlyHas: boolean) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'toggle_permission',
          data: { roleId, permissionId, hasPermission: currentlyHas }
        })
      })
      const result = await response.json()

      if (!response.ok) throw new Error(result.error)

      if (currentlyHas) {
        await logAdminAction(currentUser.email, 'remove_permission', 'role_permissions', roleId, {
          permission_id: permissionId
        })
      } else {
        await logAdminAction(currentUser.email, 'add_permission', 'role_permissions', roleId, {
          permission_id: permissionId
        })
      }

      loadData()
    } catch (error: any) {
      console.error('Error toggling permission:', error)
      alert(`Error: ${error.message}`)
    }
  }

  const getPermissionsByCategory = () => {
    const categorized: Record<string, Permission[]> = {}
    permissions.forEach(perm => {
      if (!categorized[perm.category]) {
        categorized[perm.category] = []
      }
      categorized[perm.category].push(perm)
    })
    return categorized
  }

  const getAccessiblePages = (roleId: string): string[] => {
    const rolePerms = rolePermissions[roleId] || []
    const accessiblePages: string[] = []

    Object.entries(PAGE_PERMISSIONS).forEach(([pageId, requiredPerms]) => {
      if (requiredPerms.length === 0) {
        accessiblePages.push(pageId)
      } else if (requiredPerms.some(perm => rolePerms.includes(perm))) {
        accessiblePages.push(pageId)
      }
    })

    return accessiblePages
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Loading roles and permissions...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Role & Permission Manager</h2>
          <p className="text-white mt-1">Manage admin roles, permissions, and access control</p>
        </div>
        <button
          onClick={() => {
            setRoleFormData({ role_name: '', display_name: '', description: '' })
            setShowCreateRoleModal(true)
          }}
          className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 font-medium"
        >
          ➕ Create Role
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('roles')}
          className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'roles'
              ? 'bg-yellow-500 text-gray-900'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          👥 Roles ({roles.length})
        </button>
        <button
          onClick={() => setActiveTab('permissions')}
          className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'permissions'
              ? 'bg-yellow-500 text-gray-900'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          🔐 Permissions ({permissions.length})
        </button>
        <button
          onClick={() => setActiveTab('assignments')}
          className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'assignments'
              ? 'bg-yellow-500 text-gray-900'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          🔗 Role-Permission Matrix
        </button>
      </div>

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map(role => {
            const accessiblePages = getAccessiblePages(role.id)
            const permCount = rolePermissions[role.id]?.length || 0

            return (
              <div key={role.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white">{role.display_name}</h3>
                    <p className="text-sm text-white mt-1">{role.role_name}</p>
                    {role.is_system_role && (
                      <span className="inline-block mt-2 px-2 py-1 bg-blue-500/20 text-blue-400 text-sm rounded">
                        System Role
                      </span>
                    )}
                  </div>
                  {!role.is_system_role && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setSelectedRole(role)
                          setRoleFormData({
                            role_name: role.role_name,
                            display_name: role.display_name,
                            description: role.description || ''
                          })
                          setShowEditRoleModal(true)
                        }}
                        className="text-blue-400 hover:text-blue-300"
                        title="Edit Role"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteRole(role)}
                        className="text-red-400 hover:text-red-300"
                        title="Delete Role"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>

                <p className="text-gray-300 text-sm mb-4">{role.description}</p>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-white">Permissions:</span>
                    <span className="text-yellow-500 font-medium">{permCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-white">Accessible Pages:</span>
                    <span className="text-green-500 font-medium">{accessiblePages.length}</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setSelectedRole(role)
                    setShowPermissionAssignModal(true)
                  }}
                  className="mt-4 w-full px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 text-sm"
                >
                  Manage Permissions
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* Permissions Tab */}
      {activeTab === 'permissions' && (
        <div className="space-y-6">
          {Object.entries(getPermissionsByCategory()).map(([category, perms]) => (
            <div key={category} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">{category}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {perms.map(perm => (
                  <div key={perm.id} className="bg-gray-700/50 rounded p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-white font-medium">{perm.display_name}</h4>
                        <p className="text-sm text-white mt-1">{perm.permission_key}</p>
                        <p className="text-sm text-gray-300 mt-2">{perm.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Role-Permission Matrix Tab */}
      {activeTab === 'assignments' && (
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left text-white font-semibold p-3">Permission</th>
                {roles.map(role => (
                  <th key={role.id} className="text-center text-white font-semibold p-3 min-w-[100px]">
                    <div className="text-sm">{role.display_name}</div>
                    <div className="text-sm text-white font-normal">{role.role_name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(getPermissionsByCategory()).map(([category, perms]) => (
                <React.Fragment key={category}>
                  <tr className="bg-gray-700/30">
                    <td colSpan={roles.length + 1} className="p-2 text-yellow-500 font-semibold">
                      {category}
                    </td>
                  </tr>
                  {perms.map(perm => (
                    <tr key={perm.id} className="border-b border-gray-700/50 hover:bg-gray-700/20">
                      <td className="p-3 text-gray-300">
                        <div className="text-sm">{perm.display_name}</div>
                        <div className="text-sm text-gray-500">{perm.permission_key}</div>
                      </td>
                      {roles.map(role => {
                        const hasPermission = rolePermissions[role.id]?.includes(perm.permission_key) || false
                        return (
                          <td key={role.id} className="p-3 text-center">
                            <button
                              onClick={() => handleTogglePermission(role.id, perm.id, hasPermission)}
                              className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${
                                hasPermission
                                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                  : 'bg-gray-700 text-gray-500 hover:bg-gray-600'
                              }`}
                              title={hasPermission ? 'Click to remove' : 'Click to assign'}
                            >
                              {hasPermission ? '✓' : '−'}
                            </button>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Role Modal */}
      {showCreateRoleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">Create New Role</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Role Name (Internal) *
                </label>
                <input
                  type="text"
                  value={roleFormData.role_name}
                  onChange={(e) => setRoleFormData({ ...roleFormData, role_name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="e.g., content_manager"
                />
                <p className="text-sm text-white mt-1">Lowercase, underscores only</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={roleFormData.display_name}
                  onChange={(e) => setRoleFormData({ ...roleFormData, display_name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="e.g., Content Manager"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={roleFormData.description}
                  onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  rows={3}
                  placeholder="Describe this role's responsibilities..."
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateRoleModal(false)
                  setRoleFormData({ role_name: '', display_name: '', description: '' })
                }}
                className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRole}
                disabled={!roleFormData.role_name || !roleFormData.display_name}
                className="flex-1 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditRoleModal && selectedRole && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">Edit Role</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Role Name (Internal)
                </label>
                <input
                  type="text"
                  value={roleFormData.role_name}
                  disabled
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-gray-500 cursor-not-allowed"
                />
                <p className="text-sm text-white mt-1">Cannot change role name</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={roleFormData.display_name}
                  onChange={(e) => setRoleFormData({ ...roleFormData, display_name: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={roleFormData.description}
                  onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditRoleModal(false)
                  setSelectedRole(null)
                  setRoleFormData({ role_name: '', display_name: '', description: '' })
                }}
                className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateRole}
                disabled={!roleFormData.display_name}
                className="flex-1 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permission Assignment Modal */}
      {showPermissionAssignModal && selectedRole && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-white">Manage Permissions</h3>
                <p className="text-white text-sm mt-1">{selectedRole.display_name}</p>
              </div>
              <button
                onClick={() => {
                  setShowPermissionAssignModal(false)
                  setSelectedRole(null)
                }}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {Object.entries(getPermissionsByCategory()).map(([category, perms]) => (
                <div key={category} className="bg-gray-700/30 rounded-lg p-4">
                  <h4 className="text-yellow-500 font-semibold mb-3">{category}</h4>
                  <div className="space-y-2">
                    {perms.map(perm => {
                      const hasPermission = rolePermissions[selectedRole.id]?.includes(perm.permission_key) || false
                      return (
                        <div key={perm.id} className="flex items-start space-x-3 p-2 rounded hover:bg-gray-700/50">
                          <input
                            type="checkbox"
                            checked={hasPermission}
                            onChange={() => handleTogglePermission(selectedRole.id, perm.id, hasPermission)}
                            className="mt-1 w-4 h-4 text-yellow-500 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                          />
                          <div className="flex-1">
                            <div className="text-white font-medium">{perm.display_name}</div>
                            <div className="text-sm text-white">{perm.permission_key}</div>
                            <div className="text-sm text-gray-300 mt-1">{perm.description}</div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowPermissionAssignModal(false)
                  setSelectedRole(null)
                }}
                className="px-6 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-600 font-medium"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

